import { useMemo } from "react";
import { choicesFor, MAX_LEVEL, type ExerciseKind } from "../../../learning/exercises";
import { summarize } from "../../../learning/stats";
import { usePractice, useServices } from "../../context";
import { Panel } from "../controls";

const pct = (v: number | null) => (v === null ? "—" : `${Math.round(v * 100)}%`);

/** Practice history: accuracy, response time, daily goal and a suggested focus. */
export function PracticeProgress() {
  const { practice } = useServices();
  const state = usePractice();
  const rows = useMemo(() => (["interval", "chord"] as ExerciseKind[]).map((k) => ({ kind: k, s: summarize(state.attempts[k]), level: state.staircase[k].level })), [state]);
  const today = rows.reduce((n, r) => n + r.s.today, 0);
  const label = (k: ExerciseKind, id: string) => choicesFor(k, MAX_LEVEL).find((c) => c.id === id)?.label ?? id;

  return (
    <Panel title="Practice progress">
      <label className="inline-field">
        <span>Daily goal</span>
        <input type="number" min={1} max={500} value={state.dailyGoal} aria-label="Daily goal (answers)"
          onChange={(e) => { const v = Number(e.currentTarget.value); if (Number.isFinite(v) && v >= 1) void practice.setDailyGoal(v); }} />
        <span className="unit">answers</span>
      </label>
      <p>
        <progress max={state.dailyGoal} value={Math.min(today, state.dailyGoal)} aria-label="Today's progress" /> {today} / {state.dailyGoal} today
        {today >= state.dailyGoal && <strong> — goal reached!</strong>}
      </p>
      {/* Tables cannot shrink below their content width; scroll inside the panel instead of the page. */}
      <div className="table-scroll" role="region" aria-label="Practice statistics" tabIndex={0}>
      <table className="practice-table">
        <thead><tr><th scope="col">Exercise</th><th scope="col">Level</th><th scope="col">Answers</th><th scope="col">Accuracy (last 20)</th><th scope="col">Typical response</th></tr></thead>
        <tbody>
          {rows.map(({ kind, s, level }) => (
            <tr key={kind}>
              <th scope="row">{kind === "interval" ? "Intervals" : "Chord qualities"}</th>
              <td>{level}/{MAX_LEVEL}</td>
              <td>{s.attempts}</td>
              <td>{pct(s.accuracy)} ({pct(s.recentAccuracy)})</td>
              <td>{s.medianResponseMs === null ? "—" : `${(s.medianResponseMs / 1000).toFixed(1)} s`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {rows.filter((r) => r.s.weakest).map((r) => (
        <p key={r.kind} className="callout">
          Suggested focus ({r.kind === "interval" ? "intervals" : "chords"}): <strong>{label(r.kind, r.s.weakest!.answerId)}</strong> — {pct(r.s.weakest!.accuracy)} correct over {r.s.weakest!.attempts} tries.
        </p>
      ))}
      <p className="hint">{practice.persistent ? "History is stored in this browser only." : "Local storage is unavailable, so this history will be lost on reload."}</p>
    </Panel>
  );
}
